// src/CompanyRegister/CompanyRegister.js
import React, { useState, useEffect } from "react";
import Step1 from "./Step1";
import Step2 from "./Step2";
import Step3 from "./Step3";
import Step4 from "./Step4";
import Step5 from "./Step5";
import Step6 from "./Step6";
import CompanyRegisterMenu from "./CompanyRegisterMenu";
import PageLayout from "../components/layout/PageLayout";
import { fetchWithAuth, useUser } from "../Context/UserContext";
import { useNavigate } from "react-router-dom";
import { useCompany } from "../Context/CompanyContext";
import { LoadingOverlay } from "../components/ui/loading-overlay";
import API_URL from "../config";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";


const CompanyRegister = () => {
  const { user, fetchUserInfo } = useUser();
  const { companies, fetchCompanies } = useCompany();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [errorMessage, setErrorMessage] = useState(""); // Estado para manejar el error
  const [userData, setUserData] = useState({
    nombre: user?.user.nombre || "",
    apellido: user?.user.apellido || "",
    celular: user?.user.celular || "",
    correo: user?.user.correo || "",
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/login"); // Redirige al login si no hay usuario
    } else if (companies && companies.id_company) {
      navigate(
        `/company-profile/${companies.id_company}/${companies.nombre_empresa}`
      );
    } else {
      fetchCompanies(); // Asegúrate de cargar la lista de empresas asociadas
    }
  }, []);

  const handleNext = (stepData) => {
    setFormData({ ...formData, ...stepData });
    setCurrentStep((prevStep) => prevStep + 1);
  };

  const handleBack = (stepData) => {
    setFormData({ ...formData, ...stepData });
    setCurrentStep((prevStep) => prevStep - 1);
  };

  const handleEdit = (step) => {
    setCurrentStep(step);
  };

  const normalizeData = (data) => {
    return {
      ...data,
      paginaWeb: data.paginaWeb || null,
      redesSociales: data.redesSociales || {},
      productos: data.productos || [],
      servicios: data.servicios || [],
      operacionGeografica: data.operacionGeografica || [],
      certificaciones: data.certificaciones || [], // Agregar certificaciones
    };
  };

  const handleSubmit = async (stepData) => {
    setIsLoading(true); // 🔹 Activa el cargando
    setErrorMessage(""); // Limpiar error previo
    const combinedData = { ...formData, ...stepData };
    const normalizedData = normalizeData(combinedData);

    try {
      const response = await fetchWithAuth(`${API_URL}/bs/register_company`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(normalizedData),
      });

      const responseData = await response.json(); // Extraer JSON antes de verificar response.ok

      if (!response.ok) {
        console.error("Error en la respuesta del backend:", responseData);
        setErrorMessage(responseData.error || "Ocurrió un error desconocido"); // Guardar el error
        return;
      }

      fetchUserInfo(); // Actualizar información de usuario
      fetchCompanies(); // Actualizar lista de empresas
      toast.success("Empresa registrada con éxito!");
      navigate(
        `/company-profile/${responseData.company.id_company}/${responseData.company.nombre_empresa}`
      );
      setFormData({});
    } catch (error) {
      console.error("Error al enviar los datos:", error);
      setErrorMessage("Hubo un problema al registrar la empresa."); // Guardar error en el estado
    } finally {
      setIsLoading(false); // 🔹 Desactiva el cargando
    }
  };

  if (isLoading) {
    return <LoadingOverlay />;
  }

  return (
    <PageLayout>
      <Helmet>
        <title>Registro de Empresa - Procu</title>
        <meta
          name="description"
          content="Registra tu empresa en Procu y accede a un ecosistema de proveedores y oportunidades de negocio. Completa los pasos y forma parte de la comunidad."
        />
        <meta name="keywords" content="registro empresa, Procu, proveedores, negocios, B2B, oportunidades" />
      </Helmet>
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <CompanyRegisterMenu currentStep={currentStep} />
        {currentStep === 1 && <Step1 onNext={handleNext} formData={formData} />}
        {currentStep === 2 && (
          <Step2 onNext={handleNext} onBack={handleBack} formData={formData} />
        )}
        {currentStep === 3 && (
          <Step3 onNext={handleNext} onBack={handleBack} formData={formData} />
        )}
        {currentStep === 4 && (
          <Step4 onNext={handleNext} onBack={handleBack} formData={formData} />
        )}
        {currentStep === 5 && (
          <Step5 onNext={handleNext} onBack={handleBack} formData={formData} />
        )}
        {currentStep === 6 && (
          <Step6
            onSubmit={handleSubmit}
            onBack={handleBack}
            onEdit={handleEdit} // Aquí pasamos la función onEdit
            formData={formData}
            userData={userData}
            errorMessage={errorMessage} // PASAMOS EL ERROR COMO PROP
          />
        )}
      </div>
    </PageLayout>
  );
};

export default CompanyRegister;
